import { useCallback, useEffect, useState } from 'react';

import { useAssertedParams } from '@folio/lib';
import { useSuspenseQuery } from '@tanstack/react-query';
import {
  branchDtoBranchStatusEnum,
  branchDtoBranchTypeEnum,
  type KeyValueFieldObject,
  keyValueFieldsToObject,
  policyControllerGetPolicyQueryOptions,
  policyControllerListBranchesQueryOptions,
} from 'korint-sdk';
import { useMemo } from 'react';

export function usePolicyOnDashboard<TCustomFields extends KeyValueFieldObject>() {
  const { policyId, branchId } = useAssertedParams();
  const { data: branches, refetch: refetchBranches } = useSuspenseQuery({
    ...policyControllerListBranchesQueryOptions(policyId),
  });

  const sortedBranches = useMemo(
    () =>
      branches?.data.sort(
        ({ lastModifiedAt: a }, { lastModifiedAt: b }) =>
          new Date(b).getTime() - new Date(a).getTime(),
      ) ?? [],
    [branches],
  );

  const workingBranch = useMemo(() => {
    if (!branchId) return undefined;

    return sortedBranches.find(branch => branch.id === branchId);
  }, [branchId, sortedBranches]);

  const hasOpenNewBusinessOrMtaBranch = sortedBranches.some(
    ({ branchType, branchStatus }) =>
      branchStatus === branchDtoBranchStatusEnum.BRANCH_OPEN &&
      (branchType === branchDtoBranchTypeEnum.NEW_BUSINESS ||
        branchType === branchDtoBranchTypeEnum.MTA),
  );

  // URL branch missing from the cached list: it may have been created since (a just-started stop).
  // One refetch settles it; a branch the list never has falls back to the URL param below.
  const [refetchedForBranchId, setRefetchedForBranchId] = useState<string>();
  const isBranchListStale = !!branchId && !workingBranch && refetchedForBranchId !== branchId;
  useEffect(() => {
    if (!isBranchListStale) return;
    void refetchBranches({ cancelRefetch: false }).finally(() => setRefetchedForBranchId(branchId));
  }, [isBranchListStale, refetchBranches, branchId]);

  /** Resolved branch when the list is fresh; otherwise URL param (list may lag after fork / unarchiving). */
  const branchIdForPolicy = workingBranch?.id ?? branchId;

  const { data: policy, refetch: refetchPolicy } = useSuspenseQuery({
    ...policyControllerGetPolicyQueryOptions(policyId, {
      branchId: branchIdForPolicy,
    }),
    select: data => ({
      ...data,
      customFields: keyValueFieldsToObject<TCustomFields>(data.customFields ?? []),
    }),
  });

  const { data: policyOnMain, refetch: refetchPolicyOnMain } = useSuspenseQuery({
    ...policyControllerGetPolicyQueryOptions(policyId),
    select: data => ({
      ...data,
      customFields: keyValueFieldsToObject<TCustomFields>(data.customFields ?? []),
    }),
  });

  const mutate = useCallback(
    () => Promise.all([refetchBranches(), refetchPolicy(), refetchPolicyOnMain()]),
    [refetchBranches, refetchPolicy, refetchPolicyOnMain],
  );

  return {
    policy,
    policyOnMain,
    branches,
    workingBranch,
    hasOpenNewBusinessOrMtaBranch,
    isBranchListStale,
    branchIdForPolicy,
    mutate,
  };
}
